import { DateTime } from 'luxon';
import { useMemo } from 'react';
import Linkify from 'react-linkify';
import { Link } from 'react-router-dom-v5-compat';

import AttributeItem from 'dashboard/components/AttributeItem';
import InquiryStatusInfo from 'dashboard/components/InquiryDetails/InquiryStatusInfo';
import { InfoGrid } from 'dashboard/layouts/ShowViewLayoutV2';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Inquiry, InquiryReusablePersonaStatus } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import { InquiryState } from 'dashboard/redux/reducers/inquiry';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { nameDisplay } from 'lib/utils/template';

interface InquiryInfoProps
  extends Pick<
    InquiryState,
    'reviewer' | 'reviewerWorkflow' | 'reviewerWorkflowRun' | 'reviewerWorkflowVersion'
  > {
  inquiry: Inquiry;
  template: InquiryTemplate | Template;
  creator: InquiryState['creator'];
}

const InquiryInfo = ({
  inquiry,
  template,
  reviewer,
  reviewerWorkflow,
  reviewerWorkflowRun,
  reviewerWorkflowVersion,
  creator,
}: InquiryInfoProps) => {
  const accountId = inquiry.relationships.account?.data?.id;
  const {
    'reference-id': referenceId,
    'created-at': createdAt,
    'accepted-policies-at': acceptedPoliciesAt,
  } = inquiry.attributes;
  const creatorName = userDisplayName(creator);
  const creatorDesc = creatorName == null ? '' : `by ${creatorName}`;
  const createdAtFormatted = `${DateTime.fromISO(createdAt).toFormat(
    DATE_AND_TIME_TZ_FORMAT,
  )} ${creatorDesc}`;

  const templateLink = useMemo(() => {
    const templateId = inquiry.relationships.template.data?.id;
    const inquiryTemplateId = inquiry.relationships['inquiry-template'].data?.id;
    const inquiryTemplateVersionId = inquiry.relationships['inquiry-template-version'].data?.id;

    return templateId != null
      ? `/dashboard/inquiry-templates/${templateId}`
      : `/dashboard/inquiry-templates/${inquiryTemplateId}/${inquiryTemplateVersionId}/flow`;
  }, [inquiry]);

  const hasInquiryTemplateViewPermission = usePermission(Permission.InquiryTemplateView);
  const reusablePersonasUsed =
    inquiry.attributes['reusable-persona-status'] === InquiryReusablePersonaStatus.Authenticated ||
    inquiry.attributes['reusable-persona-status'] === InquiryReusablePersonaStatus.Updated
      ? 'Yes'
      : 'No';

  return (
    <Card>
      <Card.Body>
        <Card.Title>Info</Card.Title>
        <InfoGrid>
          <AttributeItem
            title='Inquiry ID'
            value={inquiry.id}
            isRedacted={false}
            wordBreak='break-all'
            placeholder=''
            dataTest='inquiry-details-info__inquiry-id'
          />
          <AttributeItem
            title='Reference ID'
            value={referenceId}
            isRedacted={false}
            wordBreak='break-all'
            placeholder='No reference ID'
            dataTest='inquiry-details-info__reference-id'
          />
          <AttributeItem
            title='Account ID'
            value={
              accountId != null && <Link to={`/dashboard/accounts/${accountId}`}>{accountId}</Link>
            }
            isRedacted={false}
            wordBreak='break-all'
            placeholder='No account ID'
            dataTest='inquiry-details-info__account-id'
          />
          <AttributeItem
            title='Created At'
            value={createdAtFormatted}
            isRedacted={false}
            wordBreak='break-all'
            placeholder='Unknown creation date'
            dataTest='inquiry-details-info__created-at'
          />
          {acceptedPoliciesAt != null && (
            <AttributeItem
              title='Consented At'
              value={DateTime.fromISO(acceptedPoliciesAt).toFormat(DATE_AND_TIME_TZ_FORMAT)}
              isRedacted={false}
              wordBreak='break-all'
              placeholder=''
              dataTest='inquiry-details-info__consented-at'
            />
          )}
          <AttributeItem
            title='Template'
            value={
              hasInquiryTemplateViewPermission ? (
                <Link to={templateLink}>{nameDisplay(template)}</Link>
              ) : (
                nameDisplay(template)
              )
            }
            isRedacted={false}
            wordBreak='break-all'
            placeholder='No template'
            dataTest='inquiry-details-info__template'
          />
          <AttributeItem
            title='Status'
            value={
              <InquiryStatusInfo
                inquiry={inquiry}
                reviewer={reviewer}
                reviewerWorkflow={reviewerWorkflow}
                reviewerWorkflowRun={reviewerWorkflowRun}
                reviewerWorkflowVersion={reviewerWorkflowVersion}
              />
            }
            wordBreak='break-all'
            placeholder=''
            dataTest='inquiry-details-info__status'
          />
          <AttributeItem
            title='Reusable Persona used'
            value={reusablePersonasUsed}
            wordBreak='break-all'
            placeholder=''
            dataTest='inquiry-details-info__reusable-persona-status'
          />
          <AttributeItem
            title='Notes'
            value={<Linkify>{inquiry.attributes.note}</Linkify>}
            wordBreak='break-all'
            placeholder='N/A'
            dataTest='inquiry-details-info__note'
          />
        </InfoGrid>
      </Card.Body>
    </Card>
  );
};

export default InquiryInfo;
